import React from 'react';
import { Button } from "@mui/material";
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { SERVICE_IDENTIFIER } from 'constants/identifiers';
import { useTranslation } from 'hooks';
import useInjection from 'hooks/useInjection';

export const useCascadeList = (params: unknown, abortSignal: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', params],
    queryFn: () => cascadeService.getCascadeList(params, abortSignal),
  });
};

export const usePayments = (entityId: unknown, abortSignal: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'payments'],
    queryFn: () => cascadeService.getPayments(entityId, abortSignal),
  });
};

export const useFiles = (entityId: unknown, abortSignal: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'files'],
    queryFn: () => cascadeService.getFiles(entityId, abortSignal),
  });
};

export const useUploadFiles = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  return useMutation<unknown, Error, { fileType: string; files: File[] }>(
    ({ fileType, files }) => {
      return cascadeService.uploadFiles(entityId, fileType, files);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['cascades', entityId, 'files']);
        enqueueSnackbar(t('documentUploadSuccess'), { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      },
    },
  );
};

export const useDebtorInfo = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'getDebtorByCascadeId'],
    queryFn: () => cascadeService.getDebtorInfo(entityId),
  });
};

export const usePromises = (entityId: unknown, abortSignal: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'promises'],
    queryFn: () => cascadeService.getPromises(entityId, abortSignal),
  });
};

export const usePledges = (entityId: unknown, abortSignal: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId],
    queryFn: () => cascadeService.getPledges(entityId, abortSignal),
  });
};

export const useTasks = (entityId: unknown, abortSignal: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'tasks'],
    queryFn: () => cascadeService.getTasks(entityId, abortSignal),
  });
};

export const useComments = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'comments'],
    queryFn: () => cascadeService.getComments(entityId),
  });
};

export const useCreateComment = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);
  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  return useMutation<unknown[], Error, string>(
    (comment) => {
      return cascadeService.createComment(entityId, comment);
    },
    {
      onSuccess: (newComment) => {
        queryClient.setQueryData<unknown[]>(
          ['cascades', entityId, 'comments'],
          (prevValues = []) => [...prevValues, ...newComment],
        );
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      },
    },
  );
};

type CascadeTask = {
  author: { id: string | number; name: string };
  construction: { id: string | number; name: string };
  customer: { id: string | number; name: string };
  assignees: { id: string | number; name: string }[];
  created: string;
  due: string;
  content: string;
  id: string | number;
  commentCount: number;
  type: string | number;
};

const convertStructureToCascadeList = (newTask: CascadeTask) => ({
  authorId: newTask.author.id,
  authorText: newTask.author.name,
  constructionId: newTask.construction.id,
  constructionText: newTask.construction.name,
  createdAt: newTask.created,
  customerId: newTask.customer.id,
  customerText: newTask.customer.name,
  deadline: newTask.due,
  done: null,
  executorId: newTask.assignees[0].id,
  executorText: newTask.assignees[0].name,
  executors: newTask.assignees.slice(1),
  firstMessage: newTask.content,
  id: newTask.id,
  lastMessage: null,
  messagesCount: newTask.commentCount,
  timestamp: null,
  type: newTask.type.toString(),
});

export const useCreateTask = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<CascadeTask, Error, unknown>(
    (formData) => {
      return cascadeService.createTask(entityId, formData);
    },
    {
      onSuccess: (newTask) => {
        queryClient.setQueryData<ReturnType<typeof convertStructureToCascadeList>[]>(
          ['cascades', entityId, 'tasks'],
          (prevValues) => {
            return prevValues
              ? [convertStructureToCascadeList(newTask), ...prevValues]
              : [convertStructureToCascadeList(newTask)];
          },
        );

        enqueueSnackbar(t('newTaskCreated'), {
          variant: 'success',
          action: (key) => {
            return (
              <Button
                onClick={() => {
                  history.push({ pathname: `/cascades/${entityId}/tasks` });
                  closeSnackbar(key);
                }}
              >
                {t('goTo')}
              </Button>
            );
          },
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      },
    },
  );
};

export const useContacts = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'contacts'],
    queryFn: () => cascadeService.getContacts(entityId),
    placeholderData: [],
  });
};

export const useContracts = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'contracts'],
    queryFn: () => cascadeService.getContracts(entityId),
    placeholderData: [],
  });
};

export const useManagerInfo = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'managerInfo'],
    queryFn: () => cascadeService.getManagerInfo(entityId),
  });
};

export const useCreatePromise = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { t } = useTranslation();

  return useMutation<unknown, Error, unknown>(
    (formData) => {
      return cascadeService.createPromise(entityId, formData);
    },
    {
      onSuccess: () => {
        history.push({ pathname: `/cascades/${entityId}/history` });

        queryClient.invalidateQueries(['cascades', entityId, 'promises'], {
          refetchInactive: true,
        });

        enqueueSnackbar(t('newPromiseCreated'), {
          variant: 'success',
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      },
    },
  );
};

export const useCompanyCodes = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', entityId, 'companyCodes'],
    queryFn: () => cascadeService.getCompanyCodes(entityId),
    placeholderData: [],
  });
};

export const useFileTypes = (params: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);

  return useQuery({
    queryKey: ['cascades', 'fileTypes'],
    queryFn: () => cascadeService.getFileTypes(params),
    placeholderData: [],
  });
};

export const useCreateRevisionAct = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { t } = useTranslation();

  return useMutation<boolean, Error, unknown>(
    (formData) => {
      return cascadeService.createRevisionAct(entityId, formData);
    },
    {
      onSuccess: (hasEmail) => {
        history.push({ pathname: `/cascades/${entityId}/history` });
        const snackbarText = hasEmail
          ? t('attachedFileWasSent')
          : t('downloadedFile');

        enqueueSnackbar(snackbarText, {
          variant: 'success',
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      },
    },
  );
};

export const useCreateClaim = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { t } = useTranslation();

  return useMutation<boolean, Error, unknown>(
    (formData) => {
      return cascadeService.createClaim(entityId, formData);
    },
    {
      onSuccess: (hasEmail) => {
        history.push({ pathname: `/cascades/${entityId}/history` });
        const snackbarText = hasEmail
          ? t('attachedFileWasSent')
          : t('downloadedFile');

        enqueueSnackbar(snackbarText, {
          variant: 'success',
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      },
    },
  );
};

export const useCreateInvoice = (entityId: unknown) => {
  const cascadeService = useInjection(SERVICE_IDENTIFIER.CascadeService);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { t } = useTranslation();

  return useMutation<boolean, Error, unknown>(
    (formData) => {
      return cascadeService.createInvoice(entityId, formData);
    },
    {
      onSuccess: (hasEmail) => {
        history.push({ pathname: `/cascades/${entityId}/history` });
        const snackbarText = hasEmail
          ? t('attachedFileWasSent')
          : t('downloadedFile');

        enqueueSnackbar(snackbarText, {
          variant: 'success',
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      },
    },
  );
};
